import { ChevronDown, Users } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useStore } from "zustand";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { CoreStoreState, Role } from "@/lib/stores/core-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

const ASSIGNMENT_PATTERNS = [
	"cohort--",
	"team--",
	"oi-table--",
	"teaching-team-cohort--",
];

// Role ids in the current workspace held by at least one user, keyed for the
// audience picker. The broadcast audience carries role *ids* end to end
// (TECH-2934), and the names are only display labels.
function collectProgramRoleIdsInUse(
	users: CoreStoreState["users"],
	roster: Map<string, Role>,
	workspaceId: string
): Set<string> {
	const roleIds = new Set<string>();
	for (const [, entry] of users) {
		for (const ref of entry.roles) {
			if (ref.kind !== "named" || ref.workspaceId !== workspaceId) continue;
			const name = roster.get(ref.id)?.name;
			if (!name) continue;
			const isAssignment = ASSIGNMENT_PATTERNS.some((p) => name.includes(p));
			if (!isAssignment) roleIds.add(ref.id);
		}
	}
	return roleIds;
}

const PROGRAM_ROLES = [
	{ name: "learner", label: "Learner" },
	{ name: "mentor", label: "Mentor" },
	{ name: "community-rep", label: "Community Rep" },
	{ name: "judge", label: "Judge" },
	{ name: "cohort-lead", label: "Cohort Lead" },
	{ name: "teaching-team", label: "Teaching Team" },
	{ name: "tech-support", label: "Tech Support" },
	{ name: "tech-team", label: "Tech Team" },
	{ name: "program-director", label: "Program Director" },
	{ name: "program-manager", label: "Program Manager" },
	{ name: "program-operations", label: "Program Operations" },
	{ name: "receiving-tech-support", label: "Receiving Tech Support" },
];

type BroadcastRoleSelectorProps = {
	/** Role ids included in the broadcast audience. */
	includedRoles: string[];
	onIncludedRolesChange: (roleIds: string[]) => void;
};

export function BroadcastRoleSelector({
	includedRoles,
	onIncludedRolesChange,
}: BroadcastRoleSelectorProps) {
	const workspaceId = useStore(uiStore, (state) => state.currentWorkspaceId);
	const roster = useStore(coreStore, (state) => state.roles);

	const roleIdsInUse = useMemo(() => {
		if (!workspaceId) return new Set<string>();
		return collectProgramRoleIdsInUse(
			coreStore.getState().users,
			roster,
			workspaceId
		);
	}, [workspaceId, roster]);

	// Picker entries: { value: role id, label } in PROGRAM_ROLES display order.
	const availableRoles = useMemo(() => {
		const idByName = new Map<string, string>();
		for (const id of roleIdsInUse) {
			const name = roster.get(id)?.name;
			if (name) idByName.set(name, id);
		}
		return PROGRAM_ROLES.flatMap((role) => {
			const id = idByName.get(role.name);
			return id ? [{ value: id, label: role.label }] : [];
		});
	}, [roleIdsInUse, roster]);

	const initializedRef = useRef(false);
	useEffect(() => {
		if (!initializedRef.current && availableRoles.length > 0) {
			initializedRef.current = true;
			onIncludedRolesChange(availableRoles.map((r) => r.value));
		}
	}, [availableRoles, onIncludedRolesChange]);

	const handleToggle = (roleValue: string) => {
		if (includedRoles.includes(roleValue)) {
			onIncludedRolesChange(includedRoles.filter((r) => r !== roleValue));
		} else {
			onIncludedRolesChange([...includedRoles, roleValue]);
		}
	};

	const includedCount = includedRoles.length;
	const allIncluded = includedCount === availableRoles.length;

	if (availableRoles.length === 0) {
		return null;
	}

	return (
		<Collapsible className="rounded-md border">
			<CollapsibleTrigger asChild>
				<Button
					className="w-full justify-between px-3 font-normal"
					variant="ghost"
				>
					<div className="flex items-center gap-2">
						<Users className="size-4 text-muted-foreground" />
						<span>Audience</span>
						{allIncluded ? (
							<Badge className="text-xs" variant="secondary">
								All roles
							</Badge>
						) : (
							<Badge className="text-xs" variant="secondary">
								{includedCount} of {availableRoles.length} roles
							</Badge>
						)}
					</div>
					<ChevronDown className="size-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
				</Button>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<div className="border-t px-3 py-2">
					<p className="mb-2 text-muted-foreground text-xs">
						Uncheck roles to exclude them from this broadcast
					</p>
					<div className="grid grid-cols-2 gap-2">
						{availableRoles.map((role) => {
							const isIncluded = includedRoles.includes(role.value);
							return (
								<button
									className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
									key={role.value}
									onClick={() => handleToggle(role.value)}
									type="button"
								>
									<Checkbox checked={isIncluded} />
									<span className={isIncluded ? "" : "text-muted-foreground"}>
										{role.label}
									</span>
								</button>
							);
						})}
					</div>
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
}
